import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { turnstileSiteKey } from "../../util/data";
import { State, useScript } from "../../util/useScript";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

const TURNSTILE_WIDTH = 300;
const TURNSTILE_HEIGHT = 65;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "flexible" | "compact";
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const TurnstileWidgetContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${TURNSTILE_HEIGHT}px;
  margin-bottom: 15px;
  overflow: hidden;
`;

// The signup card is content-sized at narrow viewports, so an in-flow widget of
// a fixed width would widen the card, which in turn widens this container. Take
// the widget out of flow so measuring the container can never feed back into
// the container's own width.
const TurnstileMount = styled.div<{
  $scale: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${TURNSTILE_WIDTH}px;

  transform: scale(${({ $scale }) => $scale});
  transform-origin: top left;
`;

interface SignupTurnstileWidgetProps {
  onTokenChange: (token: string | null) => void;
}

export const SignupTurnstileWidget: React.ComponentType<SignupTurnstileWidgetProps> =
  React.memo(function SignupTurnstileWidget({ onTokenChange }) {
    const outerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [scale, setScale] = useState(1);
    const { state } = useScript({
      id: "cf-turnstile-script",
      src: TURNSTILE_SCRIPT_URL,
    });

    useEffect(() => {
      const outerElement = outerRef.current;
      if (outerElement == null) {
        return;
      }

      const scaleToFit = (width: number): void => {
        setScale(Math.min(1, width / TURNSTILE_WIDTH));
      };

      scaleToFit(outerElement.getBoundingClientRect().width);

      const observer = new ResizeObserver(([entry]) => {
        scaleToFit(entry.contentRect.width);
      });
      observer.observe(outerElement);

      return () => {
        observer.disconnect();
      };
    }, []);

    useEffect(() => {
      if (
        state !== State.READY ||
        turnstileSiteKey == null ||
        containerRef.current == null ||
        window.turnstile == null
      ) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => {
          onTokenChange(token);
        },
        "expired-callback": () => {
          onTokenChange(null);
        },
        "error-callback": () => {
          onTokenChange(null);
        },
        theme: "dark",
        size: "normal",
      });

      return () => {
        if (widgetIdRef.current != null && window.turnstile != null) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [state, onTokenChange]);

    if (turnstileSiteKey == null) {
      return null;
    }

    return (
      <TurnstileWidgetContainer ref={outerRef}>
        <TurnstileMount ref={containerRef} $scale={scale} />
      </TurnstileWidgetContainer>
    );
  });
